/** How a dropped pile of files reads as accounts. Shared by the dropzone
 * (badges per file) and the dialog (the "here is what we understood"
 * summary and the payload it sends). */
const ARCHIVE_RE = /\.(zip|7z)$/i;
const SESSION_RE = /\.session$/i;
const META_RE = /\.json$/i;

export interface ImportPlan {
  /** One account each — a .session or a tdata/ folder lives inside. */
  archives: File[];
  /** Loose Telethon sessions; the backend pairs each with its .json below. */
  sessions: File[];
  /** Seller metadata that found a same-named session. */
  pairedMetas: File[];
  /** Metadata with nothing to attach to, and files we don't read at all. */
  strays: File[];
}

const stem = (name: string) => name.replace(/\.[^.]+$/, "");

/**
 * Sort a dropped pile into what it means for the import, so the dialog can say
 * out loud what it understood before the operator commits to it. Mirrors the
 * backend's `_collect_import_sources` — keep the two in step.
 */
export function planImportFiles(files: File[]): ImportPlan {
  const archives = files.filter((f) => ARCHIVE_RE.test(f.name));
  const sessions = files.filter((f) => SESSION_RE.test(f.name));
  const sessionStems = new Set(sessions.map((f) => stem(f.name)));
  const metas = files.filter((f) => META_RE.test(f.name));
  const pairedMetas = metas.filter((f) => sessionStems.has(stem(f.name)));
  const paired = new Set(pairedMetas);
  const strays = files.filter(
    (f) => !ARCHIVE_RE.test(f.name) && !SESSION_RE.test(f.name) && !paired.has(f),
  );
  return { archives, sessions, pairedMetas, strays };
}

export type ImportFileKind = "archive" | "session" | "json" | "skip";

/** What one dropped file is, in the context of the whole drop. */
export function fileKind(file: File, plan: ImportPlan): ImportFileKind {
  if (ARCHIVE_RE.test(file.name)) return "archive";
  if (SESSION_RE.test(file.name)) return "session";
  if (plan.pairedMetas.includes(file)) return "json";
  return "skip";
}
